// Talking to the server. Everything goes through this object so tests can swap in an in-memory server.

const json = async (res) => {
  let body = null;
  try { body = await res.json(); } catch {}
  if (!res.ok) {
    const err = new Error(body?.error || `Server error (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
};

export function createHttpApi(base = '') {
  const req = (path, init = {}) => fetch(base + path, { credentials: 'same-origin', ...init, headers: { 'Content-Type': 'application/json', ...(init.headers || {}) } }).then(json);
  return {
    getConfig: () => req('/api/config'),
    me: () => req('/api/me'),
    login: (credential) => req('/api/auth/google', { method: 'POST', body: JSON.stringify({ credential }) }),
    logout: () => req('/api/auth/logout', { method: 'POST' }),
    getState: () => req('/api/state'),
    putState: (body) => req('/api/state', { method: 'PUT', body: JSON.stringify(body) }),
    getSheets: () => req('/api/sheets'),
    linkSheet: (pid, url) => req(`/api/sheets/${encodeURIComponent(pid)}`, { method: 'PUT', body: JSON.stringify({ url }) }),
    unlinkSheet: (pid) => req(`/api/sheets/${encodeURIComponent(pid)}`, { method: 'DELETE' }),
    syncSheet: (pid) => req(`/api/sheets/${encodeURIComponent(pid)}/sync`, { method: 'POST' }),
    readSpreadsheet: (file) => fetch(`${base}/api/xlsx/read`, { method: 'POST', body: file, credentials: 'same-origin' }).then(json),
    subscribe(onMessage, onError) {
      if (typeof EventSource !== 'function') return () => {};
      const es = new EventSource(`${base}/api/events`);
      es.onmessage = (e) => { try { onMessage(JSON.parse(e.data)); } catch {} };
      es.onerror = () => onError && onError();
      return () => es.close();
    },
  };
}

// A pretend server living in memory, for tests and for trying the app without a backend
export function createMemoryApi({ state = null, config = { auth: { google: null, required: false } }, sheets = {}, readSpreadsheet } = {}) {
  let server = { id: 'mem', version: state ? 1 : 0, data: state };
  const subs = new Set();
  let sheetView = sheets;
  const clone = (x) => JSON.parse(JSON.stringify(x));
  const notify = (msg) => subs.forEach((f) => f(msg));
  const api = {
    calls: [],
    offline: false,
    getConfig: async () => config,
    me: async () => ({ open: true }),
    login: async () => ({}),
    logout: async () => ({}),
    async getState() {
      api.calls.push('get');
      if (api.offline) throw new Error('offline');
      return clone(server);
    },
    async putState(body) {
      api.calls.push('put');
      if (api.offline) throw new Error('offline');
      if (body.id !== server.id || body.baseVersion !== server.version) {
        const err = new Error('Conflict');
        err.status = 409;
        err.body = clone(server);
        throw err;
      }
      server = { id: server.id, version: server.version + 1, data: clone(body.data) };
      return { id: server.id, version: server.version };
    },
    getSheets: async () => clone(sheetView),
    linkSheet: async () => clone(sheetView),
    unlinkSheet: async (pid) => { const v = { ...sheetView }; delete v[pid]; sheetView = v; return clone(v); },
    syncSheet: async () => clone(sheetView),
    readSpreadsheet: readSpreadsheet || (async () => { throw new Error('No reader in tests'); }),
    subscribe(onMessage) { subs.add(onMessage); return () => subs.delete(onMessage); },
    // test helpers: another device saves
    serverState: () => clone(server),
    otherDeviceSaves(fn) {
      server = { id: server.id, version: server.version + 1, data: fn(clone(server.data)) };
      notify({ id: server.id, version: server.version });
    },
    setSheets(v) { sheetView = v; notify({ sheets: Date.now() }); },
  };
  return api;
}
